import { createApi } from "@reduxjs/toolkit/query/react";

import { logout, setUser, setUserToken } from "./auth-slice";
import {
  baseQuery,
  BOOK_A_DEMO_MUTATION,
  FORGET_MUTATION,
  GET_CURRENT_USER,
  LOGIN_MUTATION,
  RESET_PASSWORD_MUTATION,
  UPDATE_USER_PASSWORD,
} from "@/graphql";
import { signedUrlApi } from "../signed-url";

interface UserReponse {
  getLoggedInUser: {
    data: IUser;
  };
}

interface LoginReponse {
  login: {
    data: AcessToken;
  };
}

export const authApi = createApi({
  reducerPath: "api/auth",
  baseQuery,
  tagTypes: ["CurrentUser"],
  endpoints: (builder) => ({
    getUserFromToken: builder.query<IUser, void>({
      query: () => ({
        document: GET_CURRENT_USER,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: user } = await queryFulfilled;

          let profileImageUrl = "";

          if (user.profileImage) {
            const { signedUrl } = await dispatch(
              signedUrlApi.endpoints.getAccessSignedUrl.initiate({
                fileName: user.profileImage,
              })
            ).unwrap();

            profileImageUrl = signedUrl;
          }

          dispatch(setUser({ ...user, profileImage: profileImageUrl }));
        } catch (error) {
          dispatch(logout());
        }
      },
      providesTags: ["CurrentUser"],
      transformResponse: (response: UserReponse) =>
        response.getLoggedInUser.data,
    }),
    login: builder.mutation<string, { email: string; password: string }>({
      query: ({ email, password }) => ({
        document: LOGIN_MUTATION,
        variables: { email, password },
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data: accessToken } = await queryFulfilled;
          dispatch(setUserToken(accessToken));
        } catch (error) {
          dispatch(logout());
        }
      },
      transformResponse: (response: LoginReponse) =>
        response.login.data.accessToken,
    }),
    resetPassword: builder.mutation<
      any,
      { newPassword: string; token: string }
    >({
      query: ({ newPassword, token }) => ({
        document: RESET_PASSWORD_MUTATION,
        variables: { newPassword, token },
      }),
    }),
    forgetPassword: builder.mutation<any, { email: string }>({
      query: ({ email }) => ({
        document: FORGET_MUTATION,
        variables: { email },
      }),
    }),
    bookADemo: builder.mutation<any, { name: string, email: string, companyName: string, firmType: string }>({
      query: ({ name, email, companyName, firmType }) => ({
        document: BOOK_A_DEMO_MUTATION,
        variables: { name, email, companyName, firmType },
      }),
    }),
    updateUserPassword :  builder.mutation<any, { email: string, newPassword: string, previousPassword: string, newUser:boolean }>({
      query: ({ email, newPassword, newUser,previousPassword }) => ({
        document: UPDATE_USER_PASSWORD,
        variables: { email, newPassword, newUser,previousPassword },
      }),
    }),
  }),
});

export const {
  useGetUserFromTokenQuery,
  useLoginMutation,
  useResetPasswordMutation,
  useForgetPasswordMutation,
  useUpdateUserPasswordMutation,
  useBookADemoMutation,
} = authApi;
